import { Navigate, Route, Routes, useLocation } from 'react-router'
import type { ReactNode } from 'react'
import { SECTIONS, type Section, type Sections } from '../shared/sections'
import { Shell } from './components/Shell'
import { FrameProvider } from './components/FrameProvider'
import { ViewingAs } from './components/ViewingAs'
import { EVERYONE, useCircleId, useFamilies, useFamily } from './lib/families'
import { circleSections } from './lib/sections'
import { usePalette } from './lib/palette'
import { savedTheme, useTheme } from './lib/theme'
import { newPath, OLD_PREFIXES } from './lib/oldPaths'
import { useMe } from './lib/useMe'
import { useMotion } from './lib/motion'
import type { Over } from './lib/photoMotion'
import { PageLoader } from './components/Loader'
import { AlbumPage } from './views/AlbumPage'
import { BookPage } from './views/BookPage'
import { CookbookEdit } from './views/CookbookEdit'
import { CookbookPage } from './views/CookbookPage'
import { CookView } from './views/CookView'
import { CalendarPage } from './views/CalendarPage'
import { ConnectLinkPage } from './views/ConnectLinkPage'
import { CircleHomePage } from './views/CircleHome'
import { OnThisDayStoryPage } from './views/OnThisDayStoryPage'
import { FamilyPage } from './views/FamilyPage'
import { AccountPage } from './views/AccountPage'
import { CircleSettingsPage, KeepACopyPage, PeopleNow, PeoplePage } from './views/PeoplePage'
import { Home, NewFamily } from './views/Home'
import { Invites } from './views/Invites'
import { Join } from './views/Join'
import { EditMoment, NewMoment } from './views/MomentForm'
import { MomentPage } from './views/MomentPage'
import { MomentsPage } from './views/MomentsPage'
import { NameStep } from './views/NameStep'
import { Onboarding } from './views/Onboarding'
import { PersonPage } from './views/PersonPage'
import { PaletteEditor } from './views/PaletteEditor'
import { PhotoPage } from './views/PhotoPage'
import { PhotosPage } from './views/PhotosPage'
import { Profile } from './views/Profile'
import { EditRecipe, NewLooseRecipe, NewRecipe } from './views/RecipeForm'
import { RecipePage } from './views/RecipePage'
import { RecipePrint } from './views/RecipePrint'
import { SignIn } from './views/SignIn'
import { StorybookPage } from './views/StorybookPage'
import { PrintBookPage } from './views/PrintBookPage'
import { PrintCardsPage } from './views/PrintCardsPage'
import { PrintCookbookPage } from './views/PrintCookbookPage'

export function App() {
  const me = useMe()
  const location = useLocation()
  const { pathname } = location
  const families = useFamilies(!!me.data)
  // Signed out, the front door is always light (Dallas, 2026-10-17), without changing this device's saved choice.
  // Until the account loads, this device's last choice stands in for the account's.
  const signedOut = me.isSuccess && !me.data
  const theme = signedOut ? 'light' : (me.data?.preferences.theme ?? savedTheme() ?? 'system')
  useTheme(theme, !signedOut)
  usePalette(me.data?.palette, theme)
  useMotion(me.data?.preferences.motion)
  if (me.isPending) return <PageLoader className="min-h-svh" />
  if (me.isError)
    return <p className="p-6 text-sm text-muted-foreground">gatheredfrom couldn’t load. Please reload the page.</p>
  // Signed out (or not yet named), any address shows sign-in first and then carries on to where it pointed,
  // so an invite link still works after signing in.
  if (!me.data) return <SignIn />
  // The app's front door, until there's a circle or your own space: the first sign-in's few steps. Elsewhere (an
  // invite link) only your name is asked first.
  if (pathname === '/') {
    if (families.isPending) return <PageLoader className="min-h-svh" />
    if (!me.data.name || families.data?.families.length === 0) {
      return (
        <>
          <Onboarding />
          <ViewingAs me={me.data} />
        </>
      )
    }
  }
  if (!me.data.name) return <NameStep />
  // A section the owner has switched off has no pages: its addresses fall through to the circle's first section. A
  // section one circle has switched off (Grow this circle) has no pages in that circle either.
  const on = me.data.sections
  // A photo opened from a page sits over that page, which stays as it was underneath (lib/photoMotion.ts).
  const background = on.photos ? (location.state as Over | null)?.background : undefined
  return (
    <FrameProvider>
      <Shell me={me.data}>
        <Routes location={background ?? location}>
          <Route path="/" element={<Home />} />
          <Route path="/circle/new" element={<NewFamily />} />
          <Route path="/circle/everyone/*" element={<ToEveryone />} />
          <Route path="/circle/:id" element={on.home ? <CircleHomePage me={me.data} /> : <FirstSection />} />
          <Route path="/everyone" element={on.home ? <CircleHomePage me={me.data} /> : <FirstSection />} />
          {on.home && (on.photos || on.moments) && (
            <>
              <Route path="/circle/:id/on-this-day/story" element={<OnThisDayStoryPage me={me.data} />} />
              <Route path="/everyone/on-this-day/story" element={<OnThisDayStoryPage me={me.data} />} />
            </>
          )}
          {on.recipes && (
            <>
              <Route
                path="/circle/:id/recipes"
                element={
                  <CircleOn section="recipes">
                    <FamilyPage me={me.data} />
                  </CircleOn>
                }
              />
              <Route
                path="/everyone/recipes"
                element={
                  <CircleOn section="recipes">
                    <FamilyPage me={me.data} />
                  </CircleOn>
                }
              />
              <Route path="/book/:id" element={<BookPage />} />
              <Route path="/book/:id/new" element={<NewRecipe />} />
              <Route
                path="/circle/:id/recipes/new"
                element={
                  <CircleOn section="recipes">
                    <NewLooseRecipe />
                  </CircleOn>
                }
              />
              <Route
                path="/everyone/recipes/new"
                element={
                  <CircleOn section="recipes">
                    <NewLooseRecipe />
                  </CircleOn>
                }
              />
              <Route path="/recipe/:id" element={<RecipePage />} />
              <Route path="/recipe/:id/edit" element={<EditRecipe />} />
              <Route path="/recipe/:id/cook" element={<CookView />} />
              <Route path="/recipe/:id/print" element={<RecipePrint />} />
              <Route path="/cookbook/:id" element={<CookbookPage />} />
              <Route path="/cookbook/:id/edit" element={<CookbookEdit />} />
              <Route path="/cookbook/:id/print" element={<PrintCookbookPage />} />
              <Route path="/cookbook/:id/cards" element={<PrintCardsPage />} />
            </>
          )}
          {/* With People off this is only the circle's page (who's here, invites, keeping a copy), with no tree. */}
          <Route path="/circle/:id/people" element={<PeoplePage me={me.data} />} />
          <Route path="/everyone/people" element={<PeoplePage me={me.data} />} />
          {on.people && (
            <>
              <Route
                path="/circle/:id/people/:personId"
                element={
                  <CircleOn section="people">
                    <PeoplePage me={me.data} />
                  </CircleOn>
                }
              />
              <Route
                path="/everyone/people/:personId"
                element={
                  <CircleOn section="people">
                    <PeoplePage me={me.data} />
                  </CircleOn>
                }
              />
              <Route path="/person/:id" element={<PersonPage />} />
            </>
          )}
          {on.photos && (
            <>
              <Route
                path="/circle/:id/photos"
                element={
                  <CircleOn section="photos">
                    <PhotosPage />
                  </CircleOn>
                }
              />
              <Route
                path="/everyone/photos"
                element={
                  <CircleOn section="photos">
                    <PhotosPage />
                  </CircleOn>
                }
              />
              <Route path="/album/:id" element={<AlbumPage />} />
              <Route path="/album/:id/story" element={<StorybookPage />} />
              <Route path="/album/:id/print" element={<PrintBookPage />} />
              <Route path="/photo/:id" element={<PhotoPage />} />
            </>
          )}
          {on.moments && (
            <>
              <Route
                path="/circle/:id/moments"
                element={
                  <CircleOn section="moments">
                    <MomentsPage />
                  </CircleOn>
                }
              />
              <Route
                path="/everyone/moments"
                element={
                  <CircleOn section="moments">
                    <MomentsPage />
                  </CircleOn>
                }
              />
              <Route
                path="/circle/:id/moments/new"
                element={
                  <CircleOn section="moments">
                    <NewMoment />
                  </CircleOn>
                }
              />
              <Route
                path="/everyone/moments/new"
                element={
                  <CircleOn section="moments">
                    <NewMoment />
                  </CircleOn>
                }
              />
              <Route path="/moment/:id" element={<MomentPage />} />
              <Route path="/moment/:id/edit" element={<EditMoment />} />
            </>
          )}
          {on.calendar && (
            <>
              <Route
                path="/circle/:id/calendar"
                element={
                  <CircleOn section="calendar">
                    <CalendarPage />
                  </CircleOn>
                }
              />
              <Route
                path="/everyone/calendar"
                element={
                  <CircleOn section="calendar">
                    <CalendarPage />
                  </CircleOn>
                }
              />
            </>
          )}
          <Route path="/circle/:id/copy" element={<KeepACopyPage />} />
          <Route path="/circle/:id/settings" element={<CircleSettingsPage me={me.data} />} />
          <Route path="/join/:token" element={<Join me={me.data} />} />
          <Route path="/me" element={<Profile me={me.data} />} />
          <Route path="/profile/:id" element={<AccountPage />} />
          <Route path="/everyone/*" element={<Navigate to="/everyone" replace />} />
          <Route path="/people" element={<PeopleNow />} />
          <Route path="/connect/:token" element={<ConnectLinkPage />} />
          {/* Preferences moved into your profile; links to the old page still land there. */}
          <Route path="/preferences" element={<Navigate to="/me" replace />} />
          {me.data.admin && <Route path="/invites" element={<Invites />} />}
          {me.data.admin && <Route path="/colours" element={<PaletteEditor me={me.data} />} />}
          {OLD_PREFIXES.map((prefix) => (
            <Route key={prefix} path={`/${prefix}/*`} element={<Moved />} />
          ))}
          <Route path="/families/new" element={<Moved />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
      {background && (
        <Routes>
          <Route path="/photo/:id" element={<PhotoPage over />} />
        </Routes>
      )}
      <ViewingAs me={me.data} />
    </FrameProvider>
  )
}

// What the circle in the address shows, once it's loaded.
function useCircleShows(): Sections | null {
  const id = useCircleId()
  const me = useMe().data
  const family = useFamily(id)
  if (!me || family.isPending) return null
  return circleSections(me.sections, family.data)
}

// With Home switched off, a circle opens on its first section that's on.
function FirstSection() {
  const id = useCircleId()
  const sections = useCircleShows()
  if (!sections) return null
  const first = SECTIONS.find((s) => s !== 'home' && sections[s]) ?? 'people'
  return <Navigate to={`${circlePath(id)}/${first}`} replace />
}

// A section's page in a circle that has it switched off goes to the circle's front page instead.
function CircleOn({ section, children }: { section: Section; children: ReactNode }) {
  const id = useCircleId()
  const sections = useCircleShows()
  if (!sections) return null
  return sections[section] ? children : <Navigate to={circlePath(id)} replace />
}

// Pages link to their circle as /circle/:id/…; Everyone's are under /everyone.
const circlePath = (id: string) => (id === EVERYONE ? '/everyone' : `/circle/${id}`)
function ToEveryone() {
  const { pathname, search, hash } = useLocation()
  return <Navigate to={pathname.replace(/^\/circle\/everyone/, '/everyone') + search + hash} replace />
}

// An address from before the word-named ones forwards to where that page lives now.
function Moved() {
  const { pathname, search, hash } = useLocation()
  return <Navigate to={(newPath(pathname) ?? '/') + search + hash} replace />
}
